import { PageMeta } from "@/components/PageMeta";
import { ScrollReveal } from "@/components/ScrollReveal";
import { CtaLink } from "@/components/Button";
import { StatusBadge } from "@/components/StatusBadge";
import { SectionHeading } from "@/components/SectionHeading";
import { Keycap } from "@/components/keycap/Keycap";
import { HeroCanvas } from "@/components/home/HeroCanvas";
import { InsightCard } from "@/components/home/InsightCard";
import { BuildSequence } from "@/components/home/BuildSequence";
import { homeInsights } from "@/content/home";
import { siteConfig, signInCta, signUpCta } from "@/config/site";
import { routeSeo } from "@/content/seo";
import "./HomePage.css";

export function HomePage() {
  const seo = routeSeo["/"];
  return (
    <div className="home">
      <PageMeta title={seo.title} description={seo.description} path="/" />

      {/* Hero — one graphic, one sentence. */}
      <section className="hero-section container">
        <p className="eyebrow hero-eyebrow">Graph-native software engineering</p>
        <h1 className="hero-title hero-title-center">
          Build software from connected, qualified components.
        </h1>
        <p className="hero-lede hero-lede-center">
          Devory turns engineering capabilities into reusable components that can be composed,
          connected, tested, executed and published through one governed platform.
        </p>
        <HeroCanvas />
        <p className="hero-stage hero-stage-center">
          <StatusBadge status="live" />
          <span>{siteConfig.stage.note}</span>
        </p>
        <div className="hero-actions hero-actions-center">
          <CtaLink to={signUpCta.path} variant="primary">
            {signUpCta.label}
          </CtaLink>
          <CtaLink to="/product" variant="secondary">
            Explore Devory
          </CtaLink>
          <CtaLink to={signInCta.path} variant="ghost">
            {signInCta.label}
          </CtaLink>
        </div>
      </section>

      {/* Why Devory exists — text-led, deliberately, before any product name. */}
      <section className="container-narrow section-tight prose" aria-labelledby="why-heading">
        <p className="eyebrow">Why Devory exists</p>
        <h2 id="why-heading">The same software gets rebuilt everywhere</h2>
        <p>
          Authentication, dashboards, payments and permissions get rewritten from scratch on every
          project, by every team. Now AI agents rebuild them too, at a speed nobody can properly
          review. Devory isn&rsquo;t a workflow tool or another AI assistant. It&rsquo;s the
          infrastructure layer where trusted components are composed by people and autonomous
          systems alike, so solved problems stay solved and the only work left is the part
          that&rsquo;s genuinely new to you.
        </p>
      </section>

      {/* The four ideas that carry the page — one screen each, no essays. */}
      <section className="container section" aria-labelledby="insights-heading">
        <h2 id="insights-heading" className="visually-hidden">
          Four ideas
        </h2>
        <div className="insights-grid">
          {homeInsights.map((insight) => (
            <InsightCard key={insight.id} insight={insight} />
          ))}
        </div>
      </section>

      {/* Qualification — trust is shown before Marketplace, never claimed after it. */}
      <section className="container section" aria-labelledby="qualify-heading">
        <SectionHeading
          level={2}
          align="center"
          eyebrow="Trust"
          title={<span id="qualify-heading">Nothing here is trusted by popularity</span>}
          lede="Every component is tested, scanned and checked before it becomes part of a graph. What follows is that same sequence, shown as it happens. Trust here is engineered, not assumed."
        />
        <ScrollReveal as="div" delay={100}>
          <BuildSequence />
        </ScrollReveal>
      </section>

      {/* Marketplace — public catalogue today, installable/commercial later. Sequenced after Trust, never before it. */}
      <section className="container section-tight" aria-labelledby="marketplace-heading">
        <div className="section-split">
          <div className="teaser-visual">
            <Keycap id="publish" size="lg" />
          </div>
          <div>
            <p className="eyebrow">Marketplace</p>
            <h2 id="marketplace-heading">A public catalogue, open today</h2>
            <p>
              Because every component is qualified first, nothing here is trusted by popularity.
              Browse the Devory Marketplace catalogue of components, collections and workflows
              without an account. Installation, publishing and commercial transactions remain
              limited to qualified or future workflows.
            </p>
            <CtaLink to="/marketplace" variant="secondary">
              Browse the Marketplace
            </CtaLink>
          </div>
        </div>
      </section>

      {/* AI — how an agent uses Devory, only once composition and trust have landed. */}
      <section className="container section-tight" aria-labelledby="ai-heading">
        <div className="section-split section-split-reverse">
          <div className="teaser-visual">
            <Keycap id="ai" size="lg" />
          </div>
          <div>
            <p className="eyebrow">For AI</p>
            <h2 id="ai-heading">Your agent composes. It doesn&rsquo;t recreate.</h2>
            <p>
              AI assistants are fast at writing code, but speed doesn&rsquo;t help much when they
              keep rebuilding the same authentication, storage and payment integrations that already
              exist. Devory gives an agent its own accountable identity and a library of qualified
              components to discover and compose, so its reasoning goes toward your actual problem
              instead of infrastructure that&rsquo;s already been solved.
            </p>
            <CtaLink to="/agent-user-interface" variant="secondary">
              See how AI uses Devory
            </CtaLink>
          </div>
        </div>
      </section>

      {/* Enterprise — one line, one visual, one link. */}
      <section className="container section-tight" aria-labelledby="enterprise-heading">
        <div className="section-split">
          <div className="teaser-visual">
            <Keycap id="enterprise" size="lg" />
          </div>
          <div>
            <p className="eyebrow">Enterprise</p>
            <h2 id="enterprise-heading">A governed canvas</h2>
            <p>
              Organisations, roles and policy are architectural, not bolted on afterward. It is the
              same trust model above, scoped to your organisation, and described honestly, without
              invented SLAs or certifications.
            </p>
            <CtaLink to="/enterprise" variant="secondary">
              Read about governance
            </CtaLink>
          </div>
        </div>
      </section>

      {/* Final call to action — one statement, not a recap. */}
      <section className="container section-tight" aria-labelledby="final-heading">
        <div className="final-cta">
          <h2 id="final-heading">Software is not written. It is composed.</h2>
          <div className="cta-banner-actions">
            <CtaLink to="/contact" variant="primary">
              Contact Devory
            </CtaLink>
            <CtaLink to="/about" variant="secondary">
              Learn about Devory
            </CtaLink>
          </div>
        </div>
      </section>
    </div>
  );
}
